import { useRef, useState } from "react";
import type { PaymentMode, PayoutView } from "@/services/types";
import { Modal } from "@/components/base/Modal";
import { Button } from "@/components/base/Button";
import { Select } from "@/components/base/Select";
import { TextField } from "@/components/base/TextField";
import { Textarea } from "@/components/base/Textarea";
import { DatePicker } from "@/components/base/DatePicker";
import { CurrencyInput } from "@/components/currency/CurrencyInput";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { PAYMENT_MODES, paymentModeLabel, referenceRequired } from "@/pages/collection/components/labels";

interface DisbursementModalProps {
  payout: PayoutView;
  onClose: () => void;
  onConfirm: (input: { amount: number; mode: PaymentMode; reference: string; paidOn: string; remarks: string }) => Promise<boolean>;
}

/**
 * Record the actual handover. The amount defaults to the balance owed (partial
 * payment is allowed). Every submit is guarded by a synchronous ref so a double
 * click can never create a duplicate disbursement for a large sum.
 */
export function DisbursementModal({ payout, onClose, onConfirm }: DisbursementModalProps) {
  const [amount, setAmount] = useState<number | null>(payout.balanceOwed);
  const [mode, setMode] = useState<PaymentMode>("cash");
  const [reference, setReference] = useState("");
  const [paidOn, setPaidOn] = useState<string>(new Date().toISOString());
  const [remarks, setRemarks] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const handleSubmit = async () => {
    if (submittingRef.current) return;
    if (referenceRequired(mode) && !reference.trim()) {
      setError("A reference number is required for this payment mode.");
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const ok = await onConfirm({ amount: amount ?? 0, mode, reference, paidOn, remarks });
      if (ok) onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not record this disbursement.");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Record disbursement"
      description={`Paying ${payout.winnerName} for ${payout.groupName}, cycle ${payout.cycle}`}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" icon={<i className="ri-hand-coin-line" />} loading={submitting} onClick={handleSubmit}>
            Confirm disbursement
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="rounded-md bg-background-100/60 px-4 py-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-foreground-500">Net payable</span>
            <span className="tnum font-medium text-foreground-900"><CurrencyDisplay amount={payout.netPayable} hideSymbol /></span>
          </div>
          <div className="mt-1 flex items-center justify-between text-sm">
            <span className="text-foreground-500">Already paid</span>
            <span className="tnum font-medium text-foreground-900"><CurrencyDisplay amount={payout.amountPaid} hideSymbol /></span>
          </div>
          <div className="mt-1 flex items-center justify-between border-t border-background-100 pt-1.5">
            <span className="text-sm font-semibold text-foreground-900">Balance now</span>
            <span className="tnum text-base font-bold text-foreground-950"><CurrencyDisplay amount={payout.balanceOwed} hideSymbol /></span>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <div className="mb-1.5 flex items-end justify-between">
              <CurrencyInput label="Amount being paid" value={amount} onChange={setAmount} required />
            </div>
            <button
              type="button"
              onClick={() => setAmount(payout.balanceOwed)}
              className="cursor-pointer text-xs font-medium text-primary-600 hover:underline"
            >
              Pay full balance
            </button>
          </div>
          <Select
            label="Payment mode"
            value={mode}
            onChange={(v) => setMode(v as PaymentMode)}
            options={PAYMENT_MODES.map((m) => ({ value: m.value, label: m.label }))}
          />
          {mode === "member_account" ? (
            <TextField
              label="Account Reference"
              placeholder="Auto-credited to wallet"
              value="Member Account Balance"
              disabled
            />
          ) : (
            <TextField
              label={referenceRequired(mode) ? "Reference number" : "Reference (optional)"}
              placeholder={referenceRequired(mode) ? "Required" : "Optional"}
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
          )}
          <DatePicker label="Payment date" value={paidOn} onChange={setPaidOn} />
        </div>

        {mode === "member_account" ? (
          <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/70 p-3 text-xs text-emerald-800">
            <i className="ri-wallet-3-line text-lg text-emerald-600" />
            <span>
              <strong>Credit to Member Account:</strong> This payout amount of{" "}
              <strong>₹{(amount ?? 0).toLocaleString("en-IN")}</strong> will be credited directly to{" "}
              {payout.winnerName}'s advance account balance for future installments or withdrawal.
            </span>
          </div>
        ) : null}

        <Textarea
          label="Remarks (optional)"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Optional note…"
          rows={2}
          maxLength={500}
        />

        {error ? <p className="text-xs text-danger-600">{error}</p> : null}

        <div className="rounded-md bg-background-100/60 px-3 py-2.5 text-xs text-foreground-600">
          A voucher number is generated and printed after you confirm. Paying less than the full balance is allowed — the remaining
          balance stays on record.
        </div>
      </div>
    </Modal>
  );
}